import * as React from "react";
import { Route, Link, Navigate } from "react-router-dom";
import { withTranslation, WithTranslation } from "react-i18next";
import { userInfoStore } from "Stores/UserInfoStore";
import { Menu, MenuProps } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GeneralUserSettingsForm } from "Pages/UserSettings/Pages/General";
import { UserSecurity } from "Pages/UserSettings/Pages/Security";
import { UserContact } from "Pages/UserSettings/Pages/Contact";
import { SwitchWith404 } from "Config/Router";
import { Widget } from "Pages/Project/Components/Widget";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { UserAuthentications } from "./Pages/UserAuthentications";
import { withRouter, WithRouterProps } from "Components/General/WithRouter";
import { last } from "lodash";
import { ProjectProfiles } from "./Pages/ProjectProfiles/ProjectProfiles";

interface UserSettingsProps extends WithTranslation, WithRouterProps {}

const NavigateHandler = observer(() => {
  if (userInfoStore.allowedToChangeSetting) {
    return <Navigate to={`/userSettings/options`} replace />;
  } else if (userInfoStore.allowedToChangePassword) {
    return <Navigate to={`/userSettings/security`} replace />;
  } else if (userInfoStore.loginInfo?.unifiedAccount) {
    return <Navigate to={`/userSettings/profiles`} replace />;
  } else {
    return <Navigate to={`/dashboard`} replace />;
  }
});

export const UserSettings = withTranslation(I18N_NS.USER_SETTINGS)(
  withRouter(
    observer(({ t, navigate, location }: UserSettingsProps) => {
      const selectedKey = React.useMemo(() => [last(location.pathname.split("/")) ?? ""], [location.pathname]);
      const menuItems: NonNullable<MenuProps["items"]> = [];

      if (userInfoStore.allowedToChangeSetting) {
        menuItems.push(
          {
            key: "options",
            label: (
              <Link to={`options`} replace={true}>
                <FontAwesomeIcon icon={["fal", "sliders-v"]} fixedWidth={true} /> {t("Settings")}
              </Link>
            ),
          },
          {
            key: "personal",
            label: (
              <Link to={`personal`} replace={true}>
                <FontAwesomeIcon icon={["fal", "id-card"]} fixedWidth={true} /> {t("Contact")}
              </Link>
            ),
          }
        );
      }

      if (userInfoStore.allowedToChangePassword) {
        menuItems.push({
          key: "security",
          label: (
            <Link to={`security`} replace={true}>
              <FontAwesomeIcon icon={["fal", "lock"]} fixedWidth={true} /> {t("Password")}
            </Link>
          ),
        });
      }

      menuItems.push({
        key: "authentications",
        label: (
          <Link to={`authentications`} replace={true}>
            <FontAwesomeIcon icon={["fal", "phone-laptop"]} fixedWidth={true} /> {t("Authentication")}
          </Link>
        ),
      });

      if (userInfoStore.loginInfo?.unifiedAccount) {
        menuItems.push({
          key: "profiles",
          label: (
            <Link to={`profiles`} replace={true}>
              <FontAwesomeIcon icon={["fal", "user-tag"]} fixedWidth={true} /> {t("Project profiles")}
            </Link>
          ),
        });
      }

      return (
        <div className="dashboard">
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="detail-action mb-2"
            style={{ width: 190 }}
          >
            <span className="detail-action-icon">
              <FontAwesomeIcon icon={["far", "chevron-left"]} fixedWidth={true} size="sm" />
            </span>
            {t("Back to dashboard")}
          </button>
          <Widget>
            <>
              <h1>{t("User settings")}</h1>
              <div className="mb-2">
                <Menu selectedKeys={selectedKey} mode="horizontal" items={menuItems} />
              </div>
              <div>
                <SwitchWith404>
                  <Route path={`/`} element={<NavigateHandler />} />
                  {userInfoStore.allowedToChangeSetting && (
                    <Route path={`options`} element={<GeneralUserSettingsForm />} />
                  )}
                  {userInfoStore.allowedToChangeSetting && <Route path={`personal`} element={<UserContact />} />}
                  {userInfoStore.allowedToChangePassword && <Route path={`security`} element={<UserSecurity />} />}
                  <Route path={`authentications`} element={<UserAuthentications />} />
                  <Route path={`profiles`} element={<ProjectProfiles />} />
                </SwitchWith404>
              </div>
            </>
          </Widget>
        </div>
      );
    })
  )
);
